import { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { slotAt, type BoardSlot } from '../lib/booking-slots';
import {
  WEEKDAYS,
  addMonths,
  clockRange,
  dayTitle,
  keyOf,
  monthGrid,
  monthTitle,
  parseKey,
  sameDay,
} from '../lib/dates';
import { cn } from '../lib/utils';
import { Alert } from './feedback/alert';
import { Button } from './shadcn/button';

/** What the calendar needs to know about the room. */
export interface CalendarRoom {
  name: string;
  /** The shortest run it takes, in minutes; `null` for one slot. */
  minimumBookingMinutes: number | null;
}

/** One slot on the board. Only the admin read says who holds a taken one. */
export interface CalendarSlot extends BoardSlot {
  booking?: { id: string; customerName: string } | null;
}

export interface Selection {
  /** Minutes from midnight; `to` exclusive. */
  from: number;
  to: number;
}

/**
 * How large the calendar draws. `default` is the admin's full page; `compact`
 * is for a dialog, where the same board has to sit inside a frame: every text a
 * step down, medium where the default is semibold, and controls at the 44px
 * minimum target instead of the 48px control height.
 */
export type CalendarSize = 'default' | 'compact';

const SIZES: Record<
  CalendarSize,
  {
    control: string;
    nav: string;
    monthTitle: string;
    weekday: string;
    cell: string;
    strong: string;
    heading: string;
    body: string;
    small: string;
    mono: string;
    button: string;
    summary: string;
    cta: string;
  }
> = {
  default: {
    control: 'min-h-(--target-control)',
    nav: 'size-12',
    monthTitle: 'text-xl font-semibold',
    weekday: 'text-sm font-semibold',
    cell: 'text-base',
    strong: 'font-semibold',
    heading: 'text-lg font-semibold',
    body: 'text-base',
    small: 'text-md',
    mono: 'text-sm',
    button: 'px-4 text-base font-semibold',
    summary: 'px-5 py-4',
    cta: 'h-(--target-control) px-8',
  },
  compact: {
    control: 'min-h-(--target-min)',
    nav: 'size-(--target-min)',
    monthTitle: 'text-lg font-medium',
    weekday: 'text-2xs font-medium',
    cell: 'text-sm',
    strong: 'font-medium',
    heading: 'text-base font-medium',
    body: 'text-sm',
    small: 'text-sm',
    mono: 'text-2xs',
    button: 'px-3 text-sm font-medium',
    summary: 'px-4 py-3',
    cta: 'h-(--target-min) px-6',
  },
};

/**
 * Month calendar beside a column of bookable time slots, for a meeting room or
 * any room booked by the slot. Used by both apps.
 *
 * Pick a day on the left, then slots on the right: click a free slot to start
 * a run, click a later one to extend it, and carry on until the run is right
 * before continuing. Given `onOpenBooking`, booked slots stay clickable so the
 * operator can open them and see who holds the room; without it they are
 * simply unavailable.
 *
 * The board is whatever the availability read returned for the day — this
 * draws it rather than deciding what the opening hours are.
 */
export default function BookingCalendar({
  resource,
  date,
  onDateChange,
  slots,
  openFromMinute,
  openToMinute,
  selection,
  onSelect,
  onOpenBooking,
  onContinue,
  showSummary = false,
  closedNote,
  size = 'default',
  disablePast = false,
  closedWeekdays = [],
  block = false,
}: {
  resource: CalendarRoom;
  /** yyyy-mm-dd */
  date: string;
  onDateChange: (next: string) => void;
  /** The API's slot board for this room on the day being shown. */
  slots: CalendarSlot[];
  /** The day's opening window, from the same read. `null` means closed. */
  openFromMinute: number | null;
  openToMinute: number | null;
  selection: Selection | null;
  onSelect: (next: Selection | null) => void;
  /** The id of the booking holding a taken slot. Leave out where bookings are not to be opened. */
  onOpenBooking?: (bookingId: string) => void;
  /** Renders the button that takes the finished run on to the booking form. */
  onContinue?: () => void;
  /**
   * Shows the line under the board (the time picked and how to extend it)
   * without the Continue button, for a host that draws its own actions, like
   * a pop-up with them in its footer. On by itself whenever `onContinue` is set.
   */
  showSummary?: boolean;
  /** Said on a closed day. Defaults to "<room> is closed on this day." */
  closedNote?: string;
  /** `compact` for a dialog: text a step down, medium rather than semibold. */
  size?: CalendarSize;
  /**
   * Greys out the days before today so they cannot be picked, and stops the
   * month going back past this one. For a guest, who can only book from today;
   * the admin keeps the past to look at.
   */
  disablePast?: boolean;
  /**
   * Weekdays the room never opens, as `Date.getDay()` numbers (0 is Sunday):
   * greyed out like the past, so nobody picks a day only to read "closed".
   * `closedWeekdays(resource.operatingHours)` from `lib/booking-slots`.
   */
  closedWeekdays?: number[];
  /**
   * Each slot is taken whole and never extended, for a space sold as one block
   * (the café hall's afternoon): the line under the board drops its "click a
   * later time" hints.
   */
  block?: boolean;
}) {
  const k = SIZES[size];
  const selected = useMemo(() => parseKey(date), [date]);
  const [month, setMonth] = useState(
    () => new Date(selected.getFullYear(), selected.getMonth(), 1),
  );
  const today = useMemo(() => new Date(), []);
  const cells = useMemo(() => monthGrid(month), [month]);
  const todayKey = keyOf(today);
  const atFirstMonth =
    disablePast &&
    month.getFullYear() * 12 + month.getMonth() <= today.getFullYear() * 12 + today.getMonth();

  // Shut for the day: the read says so with a null window, and backs it up by
  // returning no slots. Either answer alone is enough to stop drawing a board.
  const closed = openFromMinute === null || openToMinute === null || slots.length === 0;
  // Today's hours that have started are past booking. A guest calendar leaves
  // them out of the list, as the server does (a slot is free only while its
  // start is still ahead); the admin board keeps the whole day.
  const nowMinute = sameDay(selected, today) ? today.getHours() * 60 + today.getMinutes() : -1;
  const shown = disablePast ? slots.filter((s) => s.startMinute > nowMinute) : slots;
  // Open today, but every hour has gone.
  const dayOver = !closed && shown.length === 0;
  // Open, but every slot is taken: said in words under the board, so a page
  // of greyed-out times does not read as "pick one".
  const fullyBooked = !closed && !dayOver && shown.every((s) => !s.available);
  const windowLabel =
    openFromMinute === null || openToMinute === null
      ? null
      : clockRange(openFromMinute, openToMinute);

  const goToday = () => {
    setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    onDateChange(keyOf(today));
  };

  const pickDay = (d: Date) => {
    if (d.getMonth() !== month.getMonth()) setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    onDateChange(keyOf(d));
  };

  const clickSlot = (start: number, end: number) => {
    const held = slotAt(slots, start);
    if (held && !held.available) {
      if (held.booking) onOpenBooking?.(held.booking.id);
      return;
    }
    // No run yet, or the click lands before it: start a fresh one.
    if (!selection || start < selection.from) {
      onSelect({ from: start, to: end });
      return;
    }
    // Clicking the only selected slot again clears it.
    if (start === selection.from && selection.to === end) {
      onSelect(null);
      return;
    }
    // Extend, unless something is already held in between.
    const blocked = slots.some(
      (s) => s.startMinute >= selection.from && s.startMinute < end && !s.available,
    );
    onSelect(blocked ? { from: start, to: end } : { from: selection.from, to: end });
  };

  const minimum = resource.minimumBookingMinutes;
  const short = selection && minimum ? selection.to - selection.from < minimum : false;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:gap-6">
        {/* ---------------- month ---------------- */}
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <NavButton
                label="Previous month"
                className={k.nav}
                onClick={() => setMonth(addMonths(month, -1))}
                disabled={atFirstMonth}
              >
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </NavButton>
              <NavButton
                label="Next month"
                className={k.nav}
                onClick={() => setMonth(addMonths(month, 1))}
              >
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </NavButton>
            </div>
            <h3 className={cn('m-0 tracking-tight', k.monthTitle)}>{monthTitle(month)}</h3>
            <button
              type="button"
              onClick={goToday}
              className={cn(
                'rounded-xl border border-hairline bg-surface text-ink-secondary transition-colors hover:border-brand-edge hover:text-brand-text focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                k.control,
                k.button,
              )}
            >
              Today
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1.5" role="grid" aria-label={monthTitle(month)}>
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className={cn(
                  'pb-1 text-center font-mono tracking-wide text-ink-secondary uppercase',
                  k.weekday,
                )}
              >
                {d}
              </div>
            ))}

            {cells.map((d) => {
              const outside = d.getMonth() !== month.getMonth();
              const isSelected = sameDay(d, selected);
              const isToday = sameDay(d, today);
              const unavailable =
                (disablePast && keyOf(d) < todayKey) || closedWeekdays.includes(d.getDay());
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  onClick={() => pickDay(d)}
                  disabled={unavailable}
                  aria-current={isSelected ? 'date' : undefined}
                  aria-label={d.toLocaleDateString(undefined, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                  className={cn(
                    'flex items-center justify-center rounded-xl border tabular-nums transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                    k.control,
                    k.cell,
                    unavailable
                      ? 'cursor-not-allowed border-transparent bg-raised text-ink-disabled'
                      : isSelected
                      ? cn(
                          'border-cell-selected-bg bg-cell-selected-bg text-cell-selected-fg',
                          k.strong,
                        )
                      : isToday
                        ? cn('border-brand-edge bg-brand-wash text-brand-strong', k.strong)
                        : outside
                          ? 'border-transparent text-ink-muted'
                          : 'border-hairline bg-surface hover:border-brand-edge',
                  )}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------------- slots ---------------- */}
        {/* From `lg` the column is exactly the month's height: the inner box is
            taken out of the flow, so the row is sized by the month alone, then
            stretched over the whole cell, and the hours scroll inside it. A
            long day no longer makes the calendar taller than its month.
            Stacked below `lg`, the list keeps its own height cap instead. */}
        <div className="w-full min-w-0 lg:relative lg:w-[260px] lg:shrink-0">
          <div className="flex flex-col lg:absolute lg:inset-0">
            {/* Two lines on purpose: the column is too narrow for the date and the
            window side by side, and a wrapped window pushed right read as
            misaligned. */}
            <div
              className={cn(
                'mb-4 flex flex-col gap-0.5 border-b-2 border-hairline pb-2',
                k.control,
              )}
            >
              {/* Day and month read as one date, so they share one size and weight. */}
              <span className={cn(k.heading, 'font-semibold')}>
                {dayTitle(selected)} {selected.toLocaleDateString(undefined, { month: 'short' })}
              </span>
              {/* The window the API gave for this day, not a house opening time. */}
              <span className={cn('font-mono tracking-wide text-ink-muted uppercase', k.mono)}>
                {windowLabel && !closed ? windowLabel : 'Closed'}
              </span>
            </div>

            {closed || dayOver ? (
              <p
                className={cn(
                  'm-0 rounded-xl border border-hairline bg-raised px-4 py-3 text-ink-secondary',
                  k.body,
                )}
              >
                {closed
                  ? (closedNote ?? `${resource.name} is closed on this day.`)
                  : 'No times are left today. Pick another date.'}
              </p>
            ) : (
              <ol className="m-0 flex max-h-[420px] list-none flex-col gap-2 overflow-y-auto p-0 pr-1 lg:max-h-none lg:min-h-0 lg:flex-1">
                {shown.map((slot) => {
                  const start = slot.startMinute;
                  const taken = slot.available ? null : (slot.booking ?? null);
                  const inRun = selection && start >= selection.from && start < selection.to;
                  // The full hour, not its start: a list of starts ends an hour
                  // before closing, and "5:00 PM" last reads as shutting at 5.
                  const time = clockRange(start, slot.endMinute);
                  return (
                    <li key={start}>
                      <button
                        type="button"
                        onClick={() => clickSlot(start, slot.endMinute)}
                        disabled={!slot.available && !onOpenBooking}
                        aria-pressed={!!inRun}
                        aria-label={
                          slot.available
                            ? `${time}, available`
                            : taken && onOpenBooking
                              ? `${time}, booked by ${taken.customerName}. Open booking.`
                              : `${time}, booked`
                        }
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-xl border transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:-outline-offset-[3px] focus-visible:outline-(color:--focus-ring-color)',
                          k.control,
                          k.button,
                          !slot.available
                            ? 'border-warn-edge bg-warn-wash text-warn-text'
                            : inRun
                              ? 'border-cell-selected-bg bg-cell-selected-bg text-cell-selected-fg'
                              : 'border-hairline bg-surface hover:border-brand-edge',
                        )}
                      >
                        <span className="tabular-nums">{time}</span>
                        {taken && onOpenBooking ? (
                          <span className={cn('min-w-0 truncate font-medium', k.small)}>
                            {taken.customerName}
                          </span>
                        ) : (
                          !slot.available && (
                            <span className={cn('shrink-0 font-mono uppercase', k.mono)}>
                              Booked
                            </span>
                          )
                        )}
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>

      {/* The run so far, in words, with the way on. Kept out of the slot
          column: it is the one action on the board, so it gets the full
          width and room around it rather than squeezing in beside the times. */}
      {/* A day with nothing free is a warning, not a summary: the shared
          alert, in place of the grey line. */}
      {(onContinue || showSummary) && fullyBooked && !selection && (
        <Alert tone="warning" title="Fully booked">
          Nothing is free on this day. Pick another date.
        </Alert>
      )}
      {(onContinue || showSummary) && !closed && !dayOver && !(fullyBooked && !selection) && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hairline bg-raised',
            k.summary,
          )}
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            {selection ? (
              <>
                <span className={k.heading}>
                  {dayTitle(selected)} {selected.toLocaleDateString(undefined, { month: 'short' })}{' '}
                  · {clockRange(selection.from, selection.to)}
                </span>
                <span className={cn(k.body, short ? 'text-warn-text' : 'text-ink-muted')}>
                  {minutesLabel(selection.to - selection.from)}
                  {block
                    ? ''
                    : short && minimum
                      ? ` · ${resource.name} needs at least ${minutesLabel(minimum)}. Click a later time to extend.`
                      : ' · click a later time to extend'}
                </span>
              </>
            ) : (
              <>
                <span className={k.heading}>No time picked yet</span>
                <span className={cn('text-ink-muted', k.body)}>
                  {block
                    ? 'Click a free time to pick it.'
                    : `Click a free time to start, then a later one to extend.${minimum ? ` ${minutesLabel(minimum)} minimum.` : ''}`}
                </span>
              </>
            )}
          </div>
          {/* The shared primary button, so Continue looks like every other
              primary action rather than a lookalike drawn here. The admin's
              full-size board keeps its taller control height. Left out when
              the host draws its own actions (`showSummary` without it). */}
          {onContinue && (
            <Button
              size="lg"
              onClick={onContinue}
              disabled={!selection || !!short}
              className={cn('shrink-0', size === 'default' && k.cta)}
            >
              Continue
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


const minutesLabel = (m: number) =>
  m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;

function NavButton({
  label,
  onClick,
  disabled,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-xl border border-hairline bg-surface text-ink-secondary transition-colors hover:border-brand-edge hover:text-brand-text focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
}
